import axios from 'axios';
import { api } from './api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const postApi = {
  // Fetch all posts
  fetchPosts: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.isApproved !== undefined) {
        queryParams.append('isApproved', filters.isApproved);
      }
      if (filters.limit) {
        queryParams.append('limit', filters.limit);
      }
      if (filters.skip) {
        queryParams.append('skip', filters.skip);
      }

      const url = `${API_BASE_URL}/posts${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching posts:', error);
      throw error;
    }
  },

  // Get single post
  fetchPostById: async (postId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/posts/${postId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching post:', error);
      throw error;
    }
  },

  // Create new post
  createPost: async (formData) => {
    try {
      //console.log('Creating post with API_BASE_URL:', API_BASE_URL);
      const url = `${API_BASE_URL}/api/posts`;
      //console.log('Full URL:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type - browser will set it with boundary
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        
        // Try to parse as JSON, fallback to text
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorText;
        } catch (e) {
          errorMessage = errorText;
        }
        
        throw new Error(errorMessage || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  },

  // Update existing post
  updatePost: async (postId, formData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/posts/${postId}`, {
        method: 'PUT',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorText;
        } catch (e) {
          errorMessage = errorText;
        }
        
        throw new Error(errorMessage || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating post:', error);
      throw error;
    }
  },

  // Delete post
  deletePost: async (postId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting post:', error);
      throw error;
    }
  },

  // Toggle post approval
  togglePostApproval: async (postId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/posts/${postId}/toggle-approval`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error toggling post approval:', error);
      throw error;
    }
  },

  // Toggle like on post
  toggleLike: async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/posts/${id}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error toggling like:', error);
      throw error;
    }
  },

  // Add comment to post
  addComment: async (id, commentData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/posts/${id}/comment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(commentData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  },
};

// Export individual functions for backward compatibility
export const fetchPosts = postApi.fetchPosts;
export const fetchPostById = postApi.fetchPostById;
export const createPost = postApi.createPost;
export const updatePost = postApi.updatePost;
export const deletePost = postApi.deletePost;
export const togglePostApproval = postApi.togglePostApproval;
